import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Écran de chargement : la feuille noire et la feuille or de l'artichaut s'écartent,
// le cœur apparaît, puis tout se referme. Même mouvement que public/brand/artichaut-animation.html.
// Aucune dépendance : l'animation est une fonction du temps, pilotée par requestAnimationFrame.
const DURATION = 3.4;
const clamp = (x) => Math.min(1, Math.max(0, x));
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
function openness(t, delay) {
  const o0 = 0.2 + delay, o1 = 1.3 + delay, c0 = 1.9 - delay * 0.6, c1 = 3.0 - delay * 0.6;
  return t < o1 ? ease(clamp((t - o0) / (o1 - o0))) : 1 - ease(clamp((t - c0) / (c1 - c0)));
}

export default function Loading() {
  const { t } = useTranslation();
  const left = useRef(null);
  const right = useRef(null);
  const cream = useRef(null);
  const bottom = useRef(null);
  const glow = useRef(null);

  useEffect(() => {
    const draw = (time) => {
      const s = time % DURATION;
      const L = openness(s, 0), R = openness(s, 0.06), C = openness(s, 0.22), B = openness(s, 0.15);
      left.current?.setAttribute("transform", `translate(${-34 * L} ${-6 * L}) rotate(${-26 * L} 110 342)`);
      right.current?.setAttribute("transform", `translate(${34 * R} ${-6 * R}) rotate(${26 * R} 200 342)`);
      cream.current?.setAttribute("transform", `translate(${-8 * C} ${-26 * C}) rotate(${-6 * C} 120 260)`);
      bottom.current?.setAttribute("transform", `translate(${10 * B} ${26 * B}) rotate(${5 * B} 200 270)`);
      const m = (a, b) => Math.round(a + (b - a) * C);
      cream.current?.setAttribute("fill", `rgb(${m(250, 226)},${m(246, 199)},${m(236, 121)})`);
      glow.current?.setAttribute("opacity", (Math.min(C, B) ** 2 * 0.9).toFixed(3));
    };
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf;
    let start = null;
    const loop = (now) => {
      if (start === null) start = now;
      draw((now - start) / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ padding: "3rem 2rem", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
    >
      <svg viewBox="-60 -20 432 400" width="120" height="111" aria-hidden="true">
        <defs>
          <linearGradient id="ld-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#E2C779" />
            <stop offset=".5" stopColor="#C9A961" />
            <stop offset="1" stopColor="#A57A2A" />
          </linearGradient>
          <radialGradient id="ld-glow">
            <stop offset="0" stopColor="#E2C779" stopOpacity=".55" />
            <stop offset="1" stopColor="#E2C779" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle ref={glow} cx="160" cy="215" r="150" fill="url(#ld-glow)" opacity="0" />
        <path ref={left} fill="#1A1A1A" d="M166 0 84 53 0 342l118-45 73-108 15-189Z" />
        <path ref={right} fill="url(#ld-gold)" d="M181 0 226 40 312 342l-79-43-72-110-12-150Z" />
        <path ref={cream} fill="#FAF6EC" d="m120 191 45 37-24 91-71-112Z" />
        <path ref={bottom} fill="#1A1A1A" d="m165 228 36-34 35 48-71 77Z" />
      </svg>
      <span style={{ fontSize: "0.95rem", opacity: 0.6 }}>{t("common.loading.safe")}</span>
    </div>
  );
}
